import { createApp } from 'vue'
import App from './App.vue'
import router from './router';
import axios from 'axios';
import './index.css';
import ButtonPrimary from './components/form/ButtonPrimary.vue';
import ButtonSecondary from './components/form/ButtonSecondary.vue';
import ButtonAccent from './components/form/ButtonAccent.vue';
import ButtonDanger from './components/form/ButtonDanger.vue';
import InputGroup from './components/form/InputGroup';
import TextEditorGroup from './components/form/TextEditorGroup';
import SelectGroup from './components/form/SelectGroup';
import SimpleTable from './components/table/SimpleTable';
import CardDefault from './components/CardDefault';

import 'flowbite';
import EventOrganizer from './classes/EventOrganizer';
import moment from 'moment/moment';

import AscendingIcon from '../src/components/icon/AscendingIcon';
import DescendingIcon from '../src/components/icon/DescendingIcon';
import EditIcon from '../src/components/icon/EditIcon';
import DeleteIcon from '../src/components/icon/DeleteIcon';
import UploadIcon from '../src/components/icon/UploadIcon';
import InfoIcon from '../src/components/icon/InfoIcon';
import ArrowLeftIcon from '../src/components/icon/ArrowLeftIcon';
import ArrowRightIcon from '../src/components/icon/ArrowRightIcon';
import TextIcon from '../src/components/icon/TextIcon';
import ImageIcon from '../src/components/icon/ImageIcon';
import SettingIcon from '../src/components/icon/SettingIcon';
import ArrowUpIcon from '../src/components/icon/ArrowUpIcon';
import ArrowDownIcon from '../src/components/icon/ArrowDownIcon';

const app = createApp(App);
app.mixin({
    data() {
        return {
            errors: {},
        }
    },

    methods: {
        getCredentials() {
            const token = localStorage.getItem('token');
            if (token === null) {
                return false;
            }

            const items = {
                token,
            };

            return items;
        },

        saveCredentials(items = {}) {
            for (const [key, val] of Object.entries(items)) {
                localStorage.setItem(key, val);
            }
        },

        removeCredentials() {
            localStorage.removeItem('token');
        },

        async httpRequest(path, method='get', config = {}) {
            if (!config.disableLoading) {
                this.$root.setLoading(true);
            }

            const headers = {};
            if (!(config.disableAuth && config.disableAuth === true)) {
                const creItems = this.getCredentials();
                if (!creItems) {
                    if (!config.disableLoading) {
                        this.$root.setLoading(false);
                    }
                    return false;
                }

                headers.token = creItems.token;
            }

            const result = await axios({
                method,
                url: `${process.env.VUE_APP_API_URL}${path}`, 
                headers,
                data: config.data || {},
                params: config.params || {},
            });

            if (result.status !== 200) {
                if (!config.disableLoading) {
                    this.$root.setLoading(false);
                }
                return false;
            } 

            const response = result.data;
            if (!config.disableError && response.result !== 'success') {
                if (response.result !== 'field_error' && response.message) {
                    this.showErrorMessage(response.message);
                }
            } else if (response.message && response.message.length > 0) {
                this.showMessage(response.message);
            }

            if (!config.disableLoading) {
                this.$root.setLoading(false);
            }

            this.setErrors(response);

            return response;
        },

        async authenticate() {
            const creItems = this.getCredentials();
            if (!creItems) {
                router.replace('/login');
            }

            const response = await this.httpRequest('/organizer/authenticate', 'get', {
                disableError: true,
            });
            if (response.result === 'invalid_token') {
                router.replace('/login');
            }

            if (response.result === 'success') {
                const data = response.data;
                this.$root.organizer = new EventOrganizer(this, data.name, data.token);
            }
        },

        generateUUID() {
            const uuid = Math.floor(Math.random() * Date.now()).toString(16);
            return uuid;
        },
        setErrors(response) {
            this.$root.errors = {};
            if (response.result === 'field_error') {
                for (const [key, val] of Object.entries(response.errors || {})) {
                    this.$root.errors[key] = val;
                }
            }
        },

        showErrorMessage(errorMessage = '') {
            this.$root.setErrorMessage(errorMessage);
        },

        showMessage(message = '') {
            this.$root.setMessage(message);
        },

        formatDatetime(value, mode = 'date') {
            const formats = {
                date: 'DD MMMM YYYY',
            };
            return moment(value).format(formats[mode] || '');
        },

        getUserStatuses() {
            return {
                available: 'Available',
                pending: 'Pending',
                failed: 'Failed',
                suspended: 'Suspended',
            };
        },

        getEventStatuses() {
            return {
                not_ready: 'Not Ready',
                ready: 'Ready',
                ended: 'Ended',
            };
        },

        getArContentTypes() {
            return {
                details: 'Details',
                customContent: 'Custom Content',
                externalUrl: 'External URL',
                iot: 'IOT',
            };
        },

        async blobToBase64(url) {
            const data = await fetch(url);
            const blob = await data.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const result = reader.result;
                    resolve(result.split('base64,').pop())
                };
                reader.readAsDataURL(blob);
            });
        },

        async uploadImage(base64, type, extension) {
            const response = await this.httpRequest('/upload-file', 'post', {
                data: {
                    data: base64,
                    type,
                    extension,
                },
                disableLoading: true,
            });

            if (response.result !== 'success') {
                return false;
            }

            return response.data.path;
        },

        organizerReadyAction(action) {
            const timerHandler = () => {
                setTimeout(() => {
                    if (this.$root.organizer !== null) {
                        action();
                    } else {
                        timerHandler();
                    }
                }, 100);
            };

            timerHandler();
        },

        replaceUrl(data) {
            return JSON.parse(JSON.stringify(data).replace(/__host__/g, process.env.VUE_APP_API_URL));
        },
    }
});

app.component('ButtonPrimary', ButtonPrimary);
app.component('ButtonSecondary', ButtonSecondary);
app.component('ButtonAccent', ButtonAccent);
app.component('ButtonDanger', ButtonDanger);
app.component('InputGroup', InputGroup);
app.component('TextEditorGroup', TextEditorGroup);
app.component('SelectGroup', SelectGroup);
app.component('SimpleTable', SimpleTable);
app.component('CardDefault', CardDefault);
app.component('AscendingIcon', AscendingIcon);
app.component('DescendingIcon', DescendingIcon);
app.component('EditIcon', EditIcon);
app.component('DeleteIcon', DeleteIcon);
app.component('UploadIcon', UploadIcon);
app.component('InfoIcon', InfoIcon);
app.component('ArrowLeftIcon', ArrowLeftIcon);
app.component('ArrowRightIcon', ArrowRightIcon);
app.component('TextIcon', TextIcon);
app.component('ImageIcon', ImageIcon);
app.component('SettingIcon', SettingIcon);
app.component('ArrowUpIcon', ArrowUpIcon);
app.component('ArrowDownIcon', ArrowDownIcon);
app.use(router);
app.mount('#app');